import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Tone, toneClass } from '../../PhysicalBookOrder/helpers/orderDisplay';

// Icon tints are intentionally one shade lighter than the shared badge palette (toneClass in
// orderDisplay.ts) but keyed by the same Tone, so adding/removing a tone there forces an update
// here (Tailwind needs the full class strings — no dynamic names).
const iconTint: Record<Tone, string> = {
  green: 'bg-green-100 text-green-700',
  red: 'bg-red-100 text-red-700',
  amber: 'bg-amber-100 text-amber-700',
  gray: 'bg-gray-200 text-gray-600',
  blue: toneClass.blue,
};

export const Tile: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: IconDefinition;
  tone: Tone;
  sub?: string;
  alert?: boolean;
  onClick?: () => void;
  /** Tooltip for clickable tiles — where the click lands. */
  hint?: string;
}> = ({ label, value, icon, tone, sub, alert, onClick, hint }) => {
  const effectiveTone: Tone = alert ? 'red' : tone;
  return (
    <div
      onClick={onClick}
      className={`border rounded-md p-4 flex-1 min-w-[10rem] flex items-start gap-3 ${
        alert ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
      } ${onClick ? 'cursor-pointer hover:shadow' : ''}`}
      title={onClick ? hint || 'Open in All Orders' : undefined}
    >
      <span className={`shrink-0 rounded-md w-9 h-9 flex items-center justify-center ${iconTint[effectiveTone]}`}>
        <FontAwesomeIcon icon={icon} />
      </span>
      <span>
        <span className="block text-xs text-gray-500 uppercase tracking-wide">{label}</span>
        <span className={`block text-2xl font-bold ${alert ? 'text-red-700' : 'text-storyblue'}`}>{value}</span>
        {sub && <span className="block text-xs text-gray-500 mt-1">{sub}</span>}
      </span>
    </div>
  );
};

export const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="text-sm font-bold uppercase tracking-wide text-gray-600 mb-2">{title}</h3>
    <div className="flex flex-wrap gap-3">{children}</div>
  </div>
);
